import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClient, updateClient, deleteClient } from '../../services/clientService';
import { getAppointmentsByClient } from '../../services/appointmentService';
import { getNotificationsByClient } from '../../services/notificationService';
import { FiEdit2, FiTrash2, FiX, FiPlus, FiCalendar, FiBell, FiUser, FiPhone, FiMail, FiMapPin, FiFileText } from 'react-icons/fi';
import './ClientDetail.css';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [editableClient, setEditableClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  
  // Variable para las pestañas
  const [activeTab, setActiveTab] = useState('info');
  
  // Estados para citas y notificaciones
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  useEffect(() => {
    if (id) {
      fetchClient();
    } else {
      setLoading(false);
    }
  }, [id]);
  
  const fetchClient = async () => {
    try {
      setLoading(true);
      const data = await getClient(id);
      setClient(data);
      setEditableClient(JSON.parse(JSON.stringify(data))); // Copia profunda
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos del cliente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchClientAppointments = async () => {
    try {
      setLoadingAppointments(true);
      const data = await getAppointmentsByClient(id);
      setAppointments(data || []);
    } catch (err) {
      console.error('Error al cargar citas:', err);
    } finally {
      setLoadingAppointments(false);
    }
  };
  
  const fetchClientNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const data = await getNotificationsByClient(id);
      setNotifications(data || []);
    } catch (err) {
      console.error('Error al cargar notificaciones:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    if (tab === 'appointments' && appointments.length === 0 && !loadingAppointments) {
      fetchClientAppointments();
    } else if (tab === 'notifications' && notifications.length === 0 && !loadingNotifications) {
      fetchClientNotifications();
    }
  };
  
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Preparar datos para actualizar
      const updatedClient = {
        ...editableClient,
        files: editableClient.files.filter(
          file => !filesToDelete.includes(file.id)
        ),
        newFiles
      };
      
      await updateClient(id, updatedClient);
      
      // Limpiar estados
      setNewFiles([]);
      setFilesToDelete([]);
      setIsEditing(false);
      
      // Recargar datos del cliente
      fetchClient();
    } catch (err) {
      setError('Error al guardar los cambios');
      console.error(err);
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    const confirmDelete = window.confirm('¿Estás seguro de que deseas dar de baja a este cliente?');
    
    if (confirmDelete) {
      try {
        setLoading(true);
        await deleteClient(id);
        navigate('/clients');
      } catch (err) {
        setError('Error al eliminar el cliente');
        console.error(err);
        setLoading(false);
      }
    }
  };
  
  const handleEdit = () => {
    setIsEditing(true);
  };
  
  const handleCancel = () => {
    // Restaurar valores originales
    setEditableClient(JSON.parse(JSON.stringify(client)));
    setNewFiles([]);
    setFilesToDelete([]);
    setIsEditing(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableClient(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...selectedFiles]);
  };
  
  const removeExistingFile = (fileId) => {
    setFilesToDelete(prev => [...prev, fileId]);
  };
  
  const removeNewFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Si está cargando o hay un error
  if (loading && !client) {
    return (
      <div className="loading-state">
        <p>Cargando...</p>
      </div>
    );
  }
  
  if (error && !client) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={fetchClient}>Reintentar</button>
      </div>
    );
  }
  
  if (!client) {
    return (
      <div className="error-state">
        <p>Cliente no encontrado</p>
        <button onClick={() => navigate('/clients')}>Volver a la lista</button>
      </div>
    );
  }
  
  return (
    <div className="client-detail-container">
      <div className="client-detail-card">
        <h1 className="client-detail-title">{client.name}</h1>
        
        {/* Pestañas */}
        <div className="client-tabs">
          <button 
            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => handleTabChange('info')}
          >
            <span>Información</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => handleTabChange('appointments')}
          >
            <span>Historial de Citas</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => handleTabChange('notifications')}
          >
            <span>Notificaciones</span>
          </button>
        </div>
        
        {/* Contenido de la pestaña de información */}
        {activeTab === 'info' && (
          <div className="client-detail-form">
            <div className="form-group">
              <label><FiUser className="field-icon" /> Nombre</label>
              {isEditing ? (
                <div className="editable-field">
                  <input
                    type="text"
                    name="name"
                    value={editableClient.name}
                    onChange={handleInputChange}
                  />
                  <FiEdit2 className="edit-icon" />
                </div>
              ) : (
                <div className="field-value">{client.name}</div>
              )}
            </div>
            
            {/* Campo de documento */}
            <div className="form-group">
              <label><FiFileText className="field-icon" /> Documento</label>
              {isEditing ? (
                <div className="editable-field">
                  <input
                    type="text"
                    name="document"
                    value={editableClient.document || ''}
                    onChange={handleInputChange}
                  />
                  <FiEdit2 className="edit-icon" />
                </div>
              ) : (
                <div className="field-value">{client.document || '-'}</div>
              )}
            </div>
            
            <div className="form-group">
              <label><FiPhone className="field-icon" /> Celular</label>
              {isEditing ? (
                <div className="editable-field">
                  <input
                    type="text"
                    name="phone"
                    value={editableClient.phone}
                    onChange={handleInputChange}
                  />
                  <FiEdit2 className="edit-icon" />
                </div>
              ) : (
                <div className="field-value">{client.phone}</div>
              )}
            </div>
            
            <div className="form-group">
              <label><FiMail className="field-icon" /> Mail</label>
              {isEditing ? (
                <div className="editable-field">
                  <input
                    type="email"
                    name="email"
                    value={editableClient.email || ''}
                    onChange={handleInputChange}
                  />
                  <FiEdit2 className="edit-icon" />
                </div>
              ) : (
                <div className="field-value">{client.email || '-'}</div>
              )}
            </div>
            
            <div className="form-group">
              <label><FiMapPin className="field-icon" /> Dirección</label>
              {isEditing ? (
                <div className="editable-field">
                  <input
                    type="text"
                    name="address"
                    value={editableClient.address || ''}
                    onChange={handleInputChange}
                  />
                  <FiEdit2 className="edit-icon" />
                </div>
              ) : (
                <div className="field-value">{client.address || '-'}</div>
              )}
            </div>
            
            <div className="form-group">
              <label><FiFileText className="field-icon" /> Mensaje personalizado</label>
              {isEditing ? (
                <div className="editable-field">
                  <textarea
                    name="personalMessage"
                    value={editableClient.personalMessage || ''}
                    onChange={handleInputChange}
                    rows={5}
                  />
                  <FiEdit2 className="edit-icon textarea-icon" />
                </div>
              ) : (
                <div className="field-value message-content">
                  {client.personalMessage || '-'}
                </div>
              )}
            </div>
            
            <div className="form-group files-group">
              <div className="files-header">
                <label><FiFileText className="field-icon" /> Archivos ADJUNTOS</label>
                {isEditing && (
                  <button 
                    type="button" 
                    className="add-file-button"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <FiPlus />
                  </button>
                )}
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
              </div>
              
              <div className="files-list">
                {/* Archivos existentes */}
                {editableClient && editableClient.files && editableClient.files.map(file => (
                  !filesToDelete.includes(file.id) && (
                    <div key={file.id} className="file-item">
                      <span className="file-name">{file.name}</span>
                      {isEditing && (
                        <button 
                          type="button" 
                          className="remove-file-button"
                          onClick={() => removeExistingFile(file.id)}
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  )
                ))}
                
                {/* Archivos nuevos */}
                {isEditing && newFiles.map((file, index) => (
                  <div key={`new-${index}`} className="file-item new-file">
                    <span className="file-name">{file.name}</span>
                    <button 
                      type="button" 
                      className="remove-file-button"
                      onClick={() => removeNewFile(index)}
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
                
                {(!editableClient?.files || editableClient.files.length === 0) && 
                 newFiles.length === 0 && (
                  <div className="no-files-message">
                    No hay archivos adjuntos
                  </div>
                )}
              </div>
            </div>
            
            <div className="client-actions">
              {isEditing ? (
                <>
                  <button className="cancel-button" onClick={handleCancel}>
                    Cancelar
                  </button>
                  <button className="save-button" onClick={handleSave}>
                    Guardar
                  </button>
                </>
              ) : (
                <>
                  <button className="delete-button" onClick={handleDelete}>
                    Dar de baja
                  </button>
                  <button className="edit-button" onClick={handleEdit}>
                    Editar
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Contenido de la pestaña de citas */}
        {activeTab === 'appointments' && (
          <div className="tab-content">
            <h2 className="tab-title">Historial de Citas</h2>
            
            {loadingAppointments ? (
              <div className="loading-tab">Cargando citas...</div>
            ) : appointments.length > 0 ? (
              <div className="appointments-list">
                {appointments.map(appointment => (
                  <div key={appointment.id} className="history-item appointment-item">
                    <div className="history-item-header">
                      <div className="history-item-date">
                        <FiCalendar className="history-item-icon" />
                        {appointment.date} - {appointment.time}
                      </div>
                      <div className={`appointment-status ${appointment.attended ? 'status-attended' : 'status-missed'}`}>
                        {appointment.attended ? 'Asistió' : 'No asistió'}
                      </div>
                    </div>
                    <div className="history-item-content">
                      <div className="appointment-type">{appointment.type}</div>
                      <div className="appointment-trainer">Entrenador: {appointment.trainer}</div>
                      {appointment.notes && (
                        <div className="appointment-notes">{appointment.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-list-message">No hay citas registradas para este cliente</div>
            )}
          </div>
        )}
        
        {/* Contenido de la pestaña de notificaciones */}
        {activeTab === 'notifications' && (
          <div className="tab-content">
            <h2 className="tab-title">Notificaciones</h2>
            
            {loadingNotifications ? (
              <div className="loading-tab">Cargando notificaciones...</div>
            ) : notifications.length > 0 ? (
              <div className="notifications-list">
                {notifications.map(notification => (
                  <div key={notification.id} className="history-item notification-item">
                    <div className="history-item-header">
                      <div className="history-item-date">
                        <FiBell className="history-item-icon" />
                        {notification.date}
                      </div>
                    </div>
                    <div className="history-item-content">
                      <div className="notification-message">{notification.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-list-message">No hay notificaciones para este cliente</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;